import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../components/Layout';
import { sendBroadcast, getBroadcasts, cancelBroadcast } from '../lib/api';
import { Megaphone, PaperPlaneTilt, Info, Warning, Siren, Clock, Calendar, Prohibit, CheckCircle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const SEVERITIES = [
  { key: 'info',     label: 'Info',     icon: Info,      bg: 'bg-sky-50',    border: 'border-sky-400',    text: 'text-sky-800' },
  { key: 'notice',   label: 'Notice',   icon: Megaphone, bg: 'bg-blue-50',   border: 'border-brand',  text: 'text-brand' },
  { key: 'warning',  label: 'Warning',  icon: Warning,   bg: 'bg-amber-50',  border: 'border-amber-500',  text: 'text-amber-900' },
  { key: 'critical', label: 'Critical', icon: Siren,     bg: 'bg-red-50',    border: 'border-red-600',    text: 'text-red-800' },
];

export default function Broadcast() {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('notice');
  const [scheduleMode, setScheduleMode] = useState('now'); // 'now' | 'later'
  const [scheduledFor, setScheduledFor] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    try { const { data } = await getBroadcasts(); setHistory(data); } catch (_) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const { upcoming, past } = useMemo(() => {
    const u = [], p = [];
    for (const b of history) {
      if (b.status === 'scheduled') u.push(b); else p.push(b);
    }
    return { upcoming: u, past: p };
  }, [history]);

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) { toast.error('Title and message are required'); return; }
    const payload = { title, message, severity };
    if (scheduleMode === 'later') {
      if (!scheduledFor) { toast.error('Pick a future datetime'); return; }
      // input type=datetime-local returns local time — convert to UTC ISO
      const iso = new Date(scheduledFor).toISOString();
      if (new Date(iso) <= new Date()) { toast.error('Scheduled time must be in the future'); return; }
      payload.scheduled_for = iso;
    }
    const label = scheduleMode === 'later' ? `schedule "${title}" for ${new Date(scheduledFor).toLocaleString()}` : `send "${title}" now`;
    if (!window.confirm(`Confirm: ${label}?`)) return;
    setSending(true);
    try {
      const { data } = await sendBroadcast(payload);
      if (data.status === 'scheduled') {
        toast.success(`Scheduled for ${new Date(data.scheduled_for).toLocaleString()}`);
      } else {
        toast.success(`Sent — ${data.emails_sent} email(s)${data.slack_sent ? ' + Slack' : ''}`);
      }
      setTitle(''); setMessage(''); setScheduledFor(''); setScheduleMode('now'); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Broadcast failed');
    } finally { setSending(false); }
  };

  const handleCancel = async (id, title) => {
    if (!window.confirm(`Cancel scheduled broadcast "${title}"?`)) return;
    try {
      await cancelBroadcast(id);
      toast.success('Scheduled broadcast cancelled');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    }
  };

  const sevMeta = (s) => SEVERITIES.find(x => x.key === s) || SEVERITIES[0];

  // Min for datetime-local input = now + 1 min (local time, trim to minutes)
  const minLocal = useMemo(() => {
    const d = new Date(Date.now() + 60 * 1000);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);

  return (
    <Layout>
      <div className="px-6 py-8 max-w-5xl">
        <div className="flex items-center gap-2 mb-1">
          <Megaphone size={18} weight="fill" className="text-brand" />
          <span className="text-[10px] font-bold tracking-[0.2em] text-brand uppercase">Admin</span>
        </div>
        <h1 className="text-3xl font-bold tracking-tight mb-1">Broadcast</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Send an announcement to every Slack + email target configured in Location Reports → Cycle Count.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 border border-border bg-card p-5 space-y-4" data-testid="broadcast-compose">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Severity</Label>
              <div className="flex gap-2 flex-wrap mt-1">
                {SEVERITIES.map(s => {
                  const Icon = s.icon;
                  const on = severity === s.key;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setSeverity(s.key)}
                      data-testid={`broadcast-sev-${s.key}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold tracking-wider uppercase border-2 ${
                        on ? `${s.bg} ${s.border} ${s.text}` : 'bg-card border-border rounded-lg text-muted-foreground hover:border-slate-400'
                      }`}
                    >
                      <Icon size={14} weight="fill" /> {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Title</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} maxLength={140}
                placeholder="Safety inspection 3pm" data-testid="broadcast-title" />
              <div className="text-[10px] text-muted-foreground text-right mt-0.5">{title.length}/140</div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Message</Label>
              <Textarea rows={5} value={message} onChange={e => setMessage(e.target.value)} maxLength={4000}
                placeholder="Please clear aisle A by 2:45pm." data-testid="broadcast-message" />
              <div className="text-[10px] text-muted-foreground text-right mt-0.5">{message.length}/4000</div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Delivery</Label>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setScheduleMode('now')}
                  data-testid="broadcast-mode-now"
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold tracking-wider uppercase border-2 ${
                    scheduleMode === 'now' ? 'bg-brand text-white border-brand' : 'bg-card text-muted-foreground border-border'
                  }`}
                >
                  <PaperPlaneTilt size={14} /> Send now
                </button>
                <button
                  type="button"
                  onClick={() => setScheduleMode('later')}
                  data-testid="broadcast-mode-later"
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold tracking-wider uppercase border-2 ${
                    scheduleMode === 'later' ? 'bg-brand text-white border-brand' : 'bg-card text-muted-foreground border-border'
                  }`}
                >
                  <Calendar size={14} /> Schedule
                </button>
              </div>
              {scheduleMode === 'later' && (
                <div className="mt-2">
                  <Input
                    type="datetime-local"
                    min={minLocal}
                    value={scheduledFor}
                    onChange={e => setScheduledFor(e.target.value)}
                    data-testid="broadcast-scheduled-for"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">Local time; the server delivers at this moment (UTC-aware).</p>
                </div>
              )}
            </div>

            <Button onClick={handleSend} disabled={sending} data-testid="broadcast-send-btn"
              className="bg-brand hover:bg-[#001F8A] w-full">
              <PaperPlaneTilt size={14} className="mr-1" />
              {sending ? (scheduleMode === 'later' ? 'Scheduling…' : 'Sending…') : (scheduleMode === 'later' ? 'Schedule broadcast' : 'Send broadcast')}
            </Button>
          </div>

          <div className="lg:col-span-2 space-y-5">
            {upcoming.length > 0 && (
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-brand mb-3 flex items-center gap-1">
                  <Calendar size={12} weight="fill" /> Upcoming ({upcoming.length})
                </h3>
                <div className="space-y-2" data-testid="broadcast-upcoming">
                  {upcoming.map((b, i) => {
                    const m = sevMeta(b.severity);
                    const Icon = m.icon;
                    return (
                      <div key={b.id} className={`border-l-4 ${m.border} ${m.bg} p-3`} data-testid={`broadcast-upcoming-${i}`}>
                        <div className="flex items-center gap-1.5 mb-1">
                          <Icon size={12} weight="fill" className={m.text} />
                          <span className={`text-[10px] font-bold tracking-wider uppercase ${m.text}`}>{b.severity}</span>
                          <span className="text-[10px] text-muted-foreground ml-auto font-mono">
                            {new Date(b.scheduled_for).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm font-bold text-foreground">{b.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{b.message}</p>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-[10px] text-muted-foreground">By {b.sent_by_email}</p>
                          <button
                            onClick={() => handleCancel(b.id, b.title)}
                            data-testid={`broadcast-cancel-${b.id}`}
                            className="text-[10px] font-bold uppercase tracking-wider text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                          >
                            <Prohibit size={11} /> Cancel
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1">
                <Clock size={12} /> Recent broadcasts ({past.length})
              </h3>
              {past.length === 0 ? (
                <div className="border-2 border-dashed border-border p-8 text-center text-xs text-muted-foreground">
                  No broadcasts sent yet.
                </div>
              ) : (
                <div className="space-y-2" data-testid="broadcast-history">
                  {past.map((b, i) => {
                    const m = sevMeta(b.severity);
                    const Icon = m.icon;
                    const cancelled = b.status === 'cancelled';
                    return (
                      <div key={b.id || i} className={`border-l-4 ${cancelled ? 'border-border bg-muted opacity-60' : `${m.border} ${m.bg}`} p-3`} data-testid={`broadcast-row-${i}`}>
                        <div className="flex items-center gap-1.5 mb-1">
                          <Icon size={12} weight="fill" className={cancelled ? 'text-muted-foreground' : m.text} />
                          <span className={`text-[10px] font-bold tracking-wider uppercase ${cancelled ? 'text-muted-foreground' : m.text}`}>
                            {cancelled ? 'Cancelled' : b.severity}
                          </span>
                          <span className="text-[10px] text-muted-foreground ml-auto font-mono">
                            {new Date(b.sent_at || b.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className={`text-sm font-bold ${cancelled ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{b.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{b.message}</p>
                        {!cancelled && (
                          <p className="text-[10px] text-muted-foreground mt-1">
                            {b.sent_by_email} · {b.emails_sent} email{b.emails_sent !== 1 ? 's' : ''}{b.slack_sent ? ' + Slack' : ''}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
